import { useState, useRef } from "react";
import { DOCUMENT_CATEGORIES, type DocumentCategory } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { X, Upload, FileText } from "lucide-react";

interface DocumentUploadDialogProps {
  schoolId: string;
  onClose: () => void;
  onUploaded: () => void;
}

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  newsletter: "Newsletter",
  schedule: "Schedule",
  indemnity: "Indemnity",
  general: "General",
};

const ACCEPTED_TYPES = ".pdf,.png,.jpg,.jpeg,.docx,.xlsx";

export function DocumentUploadDialog({
  schoolId,
  onClose,
  onUploaded,
}: DocumentUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<DocumentCategory>("general");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await api.uploadDocument(schoolId, file, category, description || undefined);
      onUploaded();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center overflow-y-auto p-4 sm:items-start">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto sm:mt-16">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload Document
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* File picker */}
          <div>
            <label className="text-sm font-medium mb-1 block">File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="hidden"
            />
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileText className="mr-2 h-4 w-4" />
              {file ? file.name : "Choose file..."}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, PNG, JPEG, DOCX, or XLSX. Max 10 MB.
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium mb-1 block">Category</label>
            <select
              title="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {DOCUMENT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-1 block">
              Description{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description..."
              maxLength={500}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          {/* Upload button */}
          <Button onClick={handleUpload} disabled={!file || uploading}>
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
